import PropTypes from "prop-types";
import { Component } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Catches render errors inside a single onboarding step so the wizard
 * shows a recovery UI instead of a blank white screen.
 */
export class OnboardingStepErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[onboarding] step render failed", {
      step: this.props.step,
      error,
      componentStack: info?.componentStack,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.step !== this.props.step && this.state.error) {
      this.setState({ error: null });
    }
  }

  handleRetry = () => {
    this.setState({ error: null });
    this.props.onRetry?.();
  };

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div
          className="rounded-xl border border-rose-200 bg-rose-50/80 p-6 text-center space-y-3"
          data-testid="onboarding-step-error"
        >
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
            <AlertCircle size={22} className="text-rose-600" />
          </div>
          <div className="text-[15px] font-semibold text-slate-900">
            Something went wrong while loading this onboarding step
          </div>
          <p className="text-[12.5px] text-slate-600 max-w-md mx-auto">
            {error?.message || "An unexpected error occurred. You can retry this step or go back."}
          </p>
          <div className="flex items-center justify-center gap-2 pt-1">
            {this.props.onBack && (
              <Button variant="outline" size="sm" onClick={this.props.onBack} data-testid="onboarding-error-back">
                Back
              </Button>
            )}
            <Button size="sm" onClick={this.handleRetry} data-testid="onboarding-error-retry">
              Retry step
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

OnboardingStepErrorBoundary.propTypes = {
  step: PropTypes.any,
  onRetry: PropTypes.func,
  onBack: PropTypes.func,
  children: PropTypes.node,
};

export default OnboardingStepErrorBoundary;
